import { MenuIcon } from '@/components/MenuIcon';
import { isAutomation, MENU_GROUPS, TEAMS } from '@/components/Sidebar';
import type { MenuId } from '@/components/Sidebar';
import type { TeamId } from '@/tasks/task';

/**
 * この画面で紹介するもの。
 *
 * サイドバーの定義から拾うと自動化 10 件が全部並んでしまい、選ぶ前の案内としては
 * 細かすぎる。ここでは「何ができる場所か」が分かる粒度に畳んで出す。
 * @graph-connects none
 */
const HIGHLIGHTS: readonly { id: MenuId; title: string; detail: string; color: string }[] = [
  {
    id: 'tasks',
    title: 'タスク管理',
    detail: 'カレンダーと一覧で、いつ何をやるかを見る',
    color: 'var(--color-doing-border)',
  },
  {
    id: 'analysis',
    title: 'タスク分析',
    detail: '誰にどの種別が寄っているかを月単位で見る',
    color: 'var(--color-kind-meeting)',
  },
  {
    id: 'fmt-monthly',
    title: '月次経理（FMT）',
    detail: '毎月の作業の型から、その月のタスクを起こす',
    color: 'var(--color-kind-monthly)',
  },
  {
    id: 'auto-tanshin',
    title: '自動化',
    detail: '経理の自動化をここから実行し、履歴を残す',
    color: 'var(--color-kind-closing)',
  },
  {
    id: 'related-links',
    title: '関連リンク',
    detail: '業務で開くシートや別アプリをまとめておく',
    color: 'var(--color-biz-port)',
  },
];

/**
 * チームごとの色。入ったあとの画面と地続きに見えるよう、既存のパレットから取る。
 * @graph-connects none
 */
const TEAM_ACCENT: Record<TeamId, string> = {
  sft: 'var(--color-kind-monthly)',
  cpt: 'var(--color-kind-meeting)',
};

/**
 * チームごとのボタンの見た目。
 *
 * hover の色は Tailwind のクラスで指定する必要があるため（インラインの style では
 * hover を書けない）、チームごとに固定のクラスを持つ。
 * @graph-connects none
 */
const TEAM_BUTTON_CLASS: Record<TeamId, string> = {
  sft: 'hover:border-[var(--color-kind-monthly)] hover:bg-[var(--color-kind-monthly-bg)]',
  cpt: 'hover:border-[var(--color-kind-meeting)] hover:bg-[var(--color-kind-meeting-bg)]',
};

/**
 * TeamSelect の props。
 */
export interface TeamSelectProps {
  /** チームを選んだときに呼ばれる */
  onSelect: (team: TeamId) => void;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 最初に開いたときのチーム選択画面。どちらのチームを見ているかは画面全体の前提なので、中に入る前に決める
 * @graph-connects isAutomation [calls] 自動化の件数を数える
 */
export function TeamSelect({ onSelect }: TeamSelectProps) {
  // 案内に出す自動化の件数は定義から数える（増減しても書き換えずに済む）
  const automationCount = MENU_GROUPS.flatMap((group) => group.items).filter((item) =>
    isAutomation(item.id),
  ).length;

  return (
    <main className="flex min-h-screen flex-1 items-center justify-center bg-[var(--color-bg-base)] px-6 py-12">
      <div className="w-full max-w-2xl">
        <header className="text-center">
          <p className="m-0 text-xs font-bold tracking-[0.2em] text-[var(--color-text-muted)]">
            AIR CLOSET ／ 経営管理室
          </p>
          <h1 className="mt-3 mb-0 text-3xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            ADO Hub
          </h1>
          <p className="mt-2 mb-0 text-sm text-[var(--color-text-secondary)]">
            タスクと自動化をまとめる場所
          </p>
        </header>

        {/* 選ぶボタンはできるだけ簡素にする。説明は周りに置く */}
        <div className="mt-10 mb-3 text-center text-xs font-semibold tracking-wide text-[var(--color-text-muted)]">
          チームを選んでください
        </div>
        <div className="mx-auto flex max-w-xl flex-wrap justify-center gap-3">
          {TEAMS.map((team) => (
            <button
              key={team.id}
              type="button"
              onClick={() => {
                onSelect(team.id);
              }}
              // Tailwind v4 の既定はボタンでも矢印カーソルなので、押せることが伝わるよう明示する
              className={`flex min-w-52 flex-1 cursor-pointer items-center gap-3 rounded-lg border-2 border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4 text-left shadow-sm transition-all hover:shadow-md active:translate-y-px active:shadow-sm ${TEAM_BUTTON_CLASS[team.id]}`}
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-[var(--color-bg-surface)]"
                style={{ backgroundColor: TEAM_ACCENT[team.id] }}
              >
                {team.label}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[var(--color-text-primary)]">
                  {team.label}
                </span>
                <span className="mt-0.5 block text-xs text-[var(--color-text-muted)]">
                  {team.fullName}
                </span>
              </span>
              <span aria-hidden="true" className="shrink-0 text-lg text-[var(--color-text-muted)]">
                &rsaquo;
              </span>
            </button>
          ))}
        </div>

        <p className="mt-3 mb-0 text-center text-xs text-[var(--color-text-muted)]">
          選んだチームは次に開いたときも覚えています。あとから左のバーで切り替えられます
        </p>

        {/* 周りに置く案内。何ができる場所かを、選ぶ前に分かるようにする */}
        <section className="mt-12 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-6 py-6">
          <h2 className="m-0 mb-4 text-center text-xs font-bold tracking-wide text-[var(--color-text-secondary)]">
            この中でできること
          </h2>
          <ul className="m-0 grid list-none gap-x-8 gap-y-3 p-0 sm:grid-cols-2">
            {HIGHLIGHTS.map((highlight) => (
              <li key={highlight.title} className="flex items-baseline gap-2.5">
                <span className="mt-0.5 shrink-0" style={{ color: highlight.color }}>
                  <MenuIcon id={highlight.id} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-[var(--color-text-primary)]">
                    {highlight.title}
                    {highlight.title === '自動化' ? (
                      <span className="ml-1.5 text-xs tabular-nums text-[var(--color-text-muted)]">
                        {`${String(automationCount)}件`}
                      </span>
                    ) : null}
                  </span>
                  <span className="block text-xs leading-relaxed text-[var(--color-text-muted)]">
                    {highlight.detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
